/**
 * Kanban-kortti yksittäiselle tilaukselle
 */

import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { getCustomerDisplayName, formatCurrency } from "@/lib/formatters";
import { getDaysBadge } from "@/lib/order-helpers";
import {
  ORDER_TYPE_COLORS,
  FULFILLMENT_TYPE_LABELS,
  FULFILLMENT_TYPE_COLORS,
  type OrderType,
  type FulfillmentType,
} from "@/config/constants";
import type { OrderListItem } from "@/types/orders";
import { Calendar, MapPin, User } from "lucide-react";

interface OrderKanbanCardProps {
  order: OrderListItem;
}

export const OrderKanbanCard = memo(function OrderKanbanCard({
  order,
}: OrderKanbanCardProps) {
  const navigate = useNavigate();
  const daysBadge = useMemo(() => getDaysBadge(order.order_date), [order.order_date]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: order.id,
    data: { order },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "p-3 cursor-grab active:cursor-grabbing select-none",
        "hover:shadow-md transition-shadow",
        isDragging && "opacity-50 shadow-lg ring-2 ring-primary"
      )}
      onClick={() => navigate(`/orders/${order.id}`)}
    >
      {/* Header: order number + type badge */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-semibold text-sm">{order.order_number}</span>
        <div className="flex gap-1">
          <Badge
            variant="outline"
            className={cn("text-[10px] px-1.5 py-0", ORDER_TYPE_COLORS[order.order_type as OrderType])}
          >
            {order.order_type === "new_work"
              ? "🔨"
              : order.order_type === "field_work"
                ? "🔧"
                : "🔄"}
          </Badge>
          <Badge
            variant="outline"
            className={cn("text-[10px] px-1.5 py-0", FULFILLMENT_TYPE_COLORS[order.fulfillment_type as FulfillmentType])}
          >
            {FULFILLMENT_TYPE_LABELS[order.fulfillment_type as FulfillmentType]}
          </Badge>
        </div>
      </div>

      {/* Customer */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <User className="h-3 w-3 shrink-0" />
        <span className="truncate">{getCustomerDisplayName(order.customers)}</span>
      </div>

      {/* Cemetery */}
      {order.cemeteries && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {order.cemeteries.name}, {order.cemeteries.city}
          </span>
        </div>
      )}

      {/* Footer: date + amount */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", daysBadge.colorClass)}>
            {daysBadge.days} pv
          </Badge>
        </div>
        <span className="text-xs font-medium">{formatCurrency(order.total_amount)}</span>
      </div>
    </Card>
  );
});
